import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { IconMail, IconCircleCheck, IconLoader2, IconArrowRight } from "@tabler/icons-react";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import { useI18n } from "@/contexts/I18nContext";
import { api, formatApiError } from "@/lib/api";

export default function SiteObservatorio() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [subLoading, setSubLoading] = useState(false);
  const [subOk, setSubOk] = useState(false);
  const [subError, setSubError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/newsletter/latest?limit=20");
        setItems(data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const openBulletin = async (bid) => {
    try {
      const { data } = await api.get(`/public/newsletter/${bid}`);
      setSelected(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { setSubError(formatApiError(e)); }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    setSubLoading(true); setSubError(""); setSubOk(false);
    try {
      await api.post("/public/newsletter/subscribe", { email, name: name || null, role: role || null });
      setSubOk(true); setEmail(""); setName(""); setRole("");
    } catch (err) { setSubError(formatApiError(err)); }
    finally { setSubLoading(false); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(lang === "es" ? "es-HN" : "en-US", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div className="bg-paper min-h-screen">
      <SiteNav />
      <section className="border-b border-line">
        <div className="wrap py-14 md:py-20">
          <p className="eyebrow">{lang === "es" ? "Investigación" : "Research"}</p>
          <h1 className="font-display font-medium text-[clamp(32px,5vw,52px)] leading-[1.05] mt-3">
            {lang === "es" ? "Observatorio · Investigaciones" : "Observatory · Research"}
          </h1>
          <p className="text-muted text-[15px] mt-5 max-w-[46em] leading-relaxed">
            {lang === "es"
              ? "Boletín semanal (martes) con hallazgos, prácticas basadas en evidencia y notas del consultorio. Suscríbete gratis."
              : "Weekly bulletin (Tuesdays) with findings, evidence-based practices and clinic notes. Free subscription."}
          </p>

          {/* Suscribirse */}
          <form onSubmit={subscribe} className="mt-8 max-w-2xl" data-testid="obs-subscribe-form">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email" required
                className="input flex-1"
                placeholder={lang === "es" ? "Tu correo electrónico" : "Your email"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="obs-subscribe-email"
              />
              <input
                className="input sm:max-w-[180px]"
                placeholder={lang === "es" ? "Nombre (opcional)" : "Name (optional)"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="obs-subscribe-name"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <select
                className="input sm:max-w-[280px]"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                data-testid="obs-subscribe-role"
                aria-label={lang === "es" ? "Yo soy…" : "I am…"}
              >
                <option value="">{lang === "es" ? "Yo soy… (opcional)" : "I am… (optional)"}</option>
                <option value="docentes">{lang === "es" ? "Docente" : "Teacher"}</option>
                <option value="sombra">{lang === "es" ? "Maestra Sombra" : "Shadow Teacher"}</option>
                <option value="psicologa">{lang === "es" ? "Psicóloga" : "Psychologist"}</option>
                <option value="familia">{lang === "es" ? "Familia" : "Family"}</option>
                <option value="cursos">{lang === "es" ? "Interesada/o en cursos" : "Interested in courses"}</option>
                <option value="otro">{lang === "es" ? "Otro" : "Other"}</option>
              </select>
              <button type="submit" className="btn text-[13px]" disabled={subLoading} data-testid="obs-subscribe-submit">
                {subLoading ? <IconLoader2 size={13} className="animate-spin" /> : <IconMail size={13} />}
                {lang === "es" ? "Suscribirme" : "Subscribe"}
              </button>
            </div>
          </form>
          {subOk && (
            <p className="mt-3 text-oktext text-[13px] flex items-center gap-1" data-testid="obs-subscribe-ok">
              <IconCircleCheck size={14} /> {lang === "es" ? "¡Listo! Empezarás a recibir el boletín los martes." : "Done! You'll receive the bulletin on Tuesdays."}
            </p>
          )}
          {subError && <p className="mt-3 text-errtext text-[13px]" data-testid="obs-subscribe-err">{subError}</p>}
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="wrap max-w-[900px]">
          {selected ? (
            <article className="prose max-w-none" data-testid="obs-bulletin-detail">
              <button className="btn-ghost btn text-[12px] mb-4" onClick={() => setSelected(null)} data-testid="obs-back">← {lang === "es" ? "Volver a la lista" : "Back to list"}</button>
              <p className="eyebrow">{fmt(selected.sent_at)}</p>
              <h2 className="font-display text-[28px] font-medium mt-2 mb-6">{selected.subject}</h2>
              <div className="text-muted text-[15px] leading-relaxed" dangerouslySetInnerHTML={{ __html: selected.html_body }} />
            </article>
          ) : (
            <>
              <h2 className="eyebrow mb-6">{lang === "es" ? "Ediciones publicadas" : "Published editions"}</h2>
              {loading ? (
                <p className="text-tert text-[12px]">Cargando…</p>
              ) : items.length === 0 ? (
                <div className="border border-line rounded-2xl p-12 text-center" data-testid="obs-empty">
                  <IconMail size={22} className="mx-auto text-tert mb-2" />
                  <p className="text-tert text-[14px]">
                    {lang === "es" ? "Aún no publicamos boletines. Suscríbete y recibirás el primero." : "No bulletins published yet. Subscribe to get the first one."}
                  </p>
                </div>
              ) : (
                <ul className="space-y-3" data-testid="obs-list">
                  {items.map((b, idx) => (
                    <motion.li
                      key={b.id}
                      initial={{ opacity: 0, y: 6 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.03 }}
                      className="card cursor-pointer hover:border-teal-deep transition"
                      onClick={() => openBulletin(b.id)}
                      data-testid={`obs-item-${b.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-tert text-[11.5px] font-mono uppercase tracking-widest">{fmt(b.sent_at)}</p>
                          <h3 className="font-display text-[19px] font-medium mt-1 leading-snug">{b.subject}</h3>
                          {b.excerpt && (
                            <p className="text-muted text-[13.5px] mt-2 leading-relaxed" data-testid={`obs-item-excerpt-${b.id}`}>
                              {b.excerpt}
                            </p>
                          )}
                          <span className="inline-flex items-center gap-1 text-teal-deep text-[12px] font-medium mt-3">
                            {lang === "es" ? "Leer boletín completo" : "Read full bulletin"}
                            <IconArrowRight size={12} />
                          </span>
                        </div>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
